import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { HelpInfo, HelpInfoService } from '@priva/appshell';

import { CurrentUserState } from 'app/current-user/state/current-user.reducer';
import { isCurrentUserInternalUser } from 'app/current-user/state/current-user.selectors';
import { HelpLinks } from 'app/shared/help/help-links.interface';

import { ProductInfo } from './product-info.interface';

@Injectable({
    providedIn: 'root',
})
export class AnalyticsHelpInfoService implements HelpInfoService {
    private helpInfo$: Observable<HelpInfo>;
    private readonly isCurrentUserInternalUser$: Observable<boolean> =
        this.store.select(isCurrentUserInternalUser);

    constructor(
        private readonly httpClient: HttpClient,
        private readonly store: Store<CurrentUserState>,
    ) {
        this.composeHelpInfo();
    }

    get helpInfo(): Observable<HelpInfo> {
        return this.helpInfo$;
    }

    private composeHelpInfo() {
        const helpLinks$ = this.getHelpLinks();
        const productInfo$ = this.getProductInfo();

        this.helpInfo$ = combineLatest([helpLinks$, productInfo$]).pipe(
            map(([links, productInfo]) => this.compileHelp(links, productInfo)),
        );
    }

    private getProductInfo(): Observable<ProductInfo> {
        return this.httpClient
            .get<ProductInfo>('./assets/help/product-info.json')
            .pipe(catchError(() => of<ProductInfo>(null)));
    }

    private getHelpLinks(): Observable<HelpLinks> {
        return this.httpClient
            .get<HelpLinks>('./assets/help/help-links.json')
            .pipe(catchError(() => of<HelpLinks>(null)));
    }

    private compileHelp(helpLinks: HelpLinks, productInfo: ProductInfo): HelpInfo {
        const helpInfo: HelpInfo = {
            about: {
                items: [
                    {
                        title: 'APP.HELP.PRODUCT_INFO.VERSION.TITLE', // 'Application version'
                        description: productInfo.version,
                    },
                ],
            },
            support: {
                description: ' ', // add empty condition-input-text to prevent appshell warning
                items: [
                    {
                        links: [
                            {
                                title: 'APP.HELP.HELP_INFO.HELP_CENTER.TITLE', // 'Help Center'
                                uri: helpLinks.helpCenter,
                            },
                        ],
                    },
                ],
            },
        };

        const emptySupportItemLink = {
            title: '',
            uri: '',
        };

        this.isCurrentUserInternalUser$.subscribe((isInternalUser) => {
            if (!isInternalUser) {
                helpInfo.support.items[0].links.push(emptySupportItemLink);
            }
        });

        return helpInfo;
    }
}
